import {
    useState,
    useMemo
} from "react";
import {
    type LayoutChangeEvent,
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    MicrosoftColorBrandIcon,
    type BrandIconComponent,
    GoogleColorBrandIcon,
    DirectionProvider,
    SpotifyBrandIcon,
    YouTubeBrandIcon,
    GitHubBrandIcon,
    NCoreUIKitTheme,
    AppleBrandIcon,
    BRAND_ICONS,
    ToggleGroup,
    XBrandIcon,
    TextInput,
    Switch,
    Button,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon
} from "../../anatomy/icons";

const NAME = "BrandIcons";

const USAGE_CODE = `import {
    SpotifyBrandIcon,
    GitHubBrandIcon,
    Icon
} from "ncore-ui-kit";

<Icon
    icon={GitHubBrandIcon}
    size="large"
/>

<Icon
    customColor={SpotifyBrandIcon.brandColor}
    accessibilityLabel={SpotifyBrandIcon.brandTitle}
    icon={SpotifyBrandIcon}
/>`;

const SIGN_IN_CODE = `import {
    GoogleColorBrandIcon,
    Button
} from "ncore-ui-kit";

<Button
    icon={GoogleColorBrandIcon}
    title="Continue with Google"
    onPress={handleGoogleSignIn}
    spreadBehaviour="stretch"
    variant="outline"
    type="neutral"
/>`;

const GALLERY_SIZES = [
    16,
    24,
    48
] as const;

const OVERVIEW_ICONS: Array<BrandIconComponent> = [
    GoogleColorBrandIcon,
    MicrosoftColorBrandIcon,
    AppleBrandIcon,
    GitHubBrandIcon,
    XBrandIcon,
    YouTubeBrandIcon,
    SpotifyBrandIcon
];

const MIN_MARK_CONTRAST = 3;

const stylesheet = StyleSheet.create({
    slot: {
        justifyContent: "center",
        alignItems: "center"
    },
    controls: {
        alignItems: "flex-end",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    gallery: {
        flexDirection: "row",
        flexWrap: "wrap",
        width: "100%"
    },
    tile: {
        alignItems: "center"
    },
    caption: {
        textAlign: "center"
    },
    direction: {
        width: "100%"
    },
    search: {
        flexGrow: 1
    }
});

const getChannel = (value: number) => {
    const channel = value / 255;

    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
};

const getLuminance = (color: string): number | undefined => {
    const match = /^#([0-9a-f]{6})$/i.exec(color);

    if(!match?.[1]) {
        return undefined;
    }

    const value = parseInt(match[1], 16);

    return 0.2126 * getChannel((value >> 16) & 255) + 0.7152 * getChannel((value >> 8) & 255) + 0.0722 * getChannel(value & 255);
};

const isVisibleOn = (color: string, background: string) => {
    const first = getLuminance(color);

    const second = getLuminance(background);

    if(first === undefined || second === undefined) {
        return true;
    }

    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05) >= MIN_MARK_CONTRAST;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("brandIconsPage.galleryTitle"),
        id: "gallery"
    },
    {
        title: t("brandIconsPage.signInTitle"),
        id: "sign-in"
    },
    {
        title: t("brandIconsPage.rulesTitle"),
        id: "rules"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("brandIconsPage.apiTitle"),
        id: "api"
    }
], NAME, t);

const BrandIconsPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        colorMode,
        setColorMode
    ] = useState("icon");

    const [
        query,
        setQuery
    ] = useState("");

    const [
        size,
        setSize
    ] = useState("24");

    const [
        isRtl,
        setIsRtl
    ] = useState(false);

    const [
        galleryWidth,
        setGalleryWidth
    ] = useState(0);

    const minTileWidth = spaces.spacingXxl + spaces.spacingXl;

    const columnCount = Math.max(1, Math.floor(galleryWidth / minTileWidth));

    const tileWidth = galleryWidth ? Math.floor(galleryWidth / columnCount) : minTileWidth;

    const filteredIcons = useMemo(() => {
        const search = query.trim().toLocaleLowerCase();

        if(!search) {
            return BRAND_ICONS;
        }

        return BRAND_ICONS.filter((icon) => icon.brandTitle.toLocaleLowerCase().includes(search) || (icon.displayName ?? "").toLocaleLowerCase().includes(search));
    }, [
        query
    ]);

    const handleColorModeChange = (value: string | Array<string>) => {
        if(typeof value === "string" && value) {
            setColorMode(value);
        }
    };

    const handleSizeChange = (value: string | Array<string>) => {
        if(typeof value === "string" && value) {
            setSize(value);
        }
    };

    const handleGalleryLayout = (event: LayoutChangeEvent) => {
        setGalleryWidth(event.nativeEvent.layout.width);
    };

    const getTileLabel = (icon: BrandIconComponent) => {
        if(icon.isMultiColor) {
            return t("brandIconsPage.colorName", [
                icon.brandTitle
            ]);
        }

        return icon.brandTitle;
    };

    const getTileColor = (icon: BrandIconComponent) => {
        if(colorMode !== "brand" || icon.isMultiColor) {
            return undefined;
        }

        if(!isVisibleOn(icon.brandColor, colors.content.container.mid)) {
            return undefined;
        }

        return icon.brandColor;
    };

    const renderTile = (icon: BrandIconComponent) => <View
        style={[
            stylesheet.tile,
            {
                paddingVertical: spaces.spacingMd,
                gap: spaces.spacingSm,
                width: tileWidth
            }
        ]}
        key={icon.displayName}
    >
        <View
            style={[
                stylesheet.slot,
                {
                    height: GALLERY_SIZES[GALLERY_SIZES.length - 1]
                }
            ]}
        >
            <Icon
                customColor={getTileColor(icon)}
                size={Number(size)}
                icon={icon}
            />
        </View>
        <Text
            style={stylesheet.caption}
            variant="bodySmallSize"
            numberOfLines={2}
            color="mid"
        >
            {getTileLabel(icon)}
        </Text>
    </View>;

    const renderGallery = () => {
        if(!filteredIcons.length) {
            return <Text
                variant="bodyMediumSize"
                color="mid"
            >
                {t("brandIconsPage.empty")}
            </Text>;
        }

        return <DirectionProvider
            direction={isRtl ? "rtl" : "ltr"}
            style={stylesheet.direction}
        >
            <View
                onLayout={handleGalleryLayout}
                style={stylesheet.gallery}
            >
                {filteredIcons.map(renderTile)}
            </View>
        </DirectionProvider>;
    };

    return <Article>
        <ComponentHeader
            title={t("brandIconsPage.title")}
            lead={t("brandIconsPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("brandIconsPage.overviewText")}
            </Paragraph>
            <Preview>
                {
                    OVERVIEW_ICONS.map((icon) => <Icon
                        key={icon.displayName}
                        icon={icon}
                        size={32}
                    />)
                }
            </Preview>
            <Paragraph>
                {t("brandIconsPage.colorText")}
            </Paragraph>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("brandIconsPage.galleryTitle")}
            id="gallery"
        >
            <Paragraph>
                {t("brandIconsPage.galleryText")}
            </Paragraph>
            <View
                style={[
                    stylesheet.controls,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.search,
                        {
                            minWidth: shapeConfigs.fieldMinWidth
                        }
                    ]}
                >
                    <TextInput
                        placeholder={t("brandIconsPage.searchPlaceholder")}
                        title={t("brandIconsPage.searchTitle")}
                        spreadBehaviour="stretch"
                        onChangeText={setQuery}
                        isClearEnabled={true}
                        icon={SearchIcon}
                        value={query}
                    />
                </View>
                <ToggleGroup
                    title={t("brandIconsPage.colorModeTitle")}
                    onValueChange={handleColorModeChange}
                    attachBehaviour="attached"
                    isClearEnabled={false}
                    variant="outline"
                    value={colorMode}
                >
                    <ToggleGroup.Item
                        title={t("brandIconsPage.iconColor")}
                        value="icon"
                    />
                    <ToggleGroup.Item
                        title={t("brandIconsPage.brandColor")}
                        value="brand"
                    />
                </ToggleGroup>
                <ToggleGroup
                    title={t("brandIconsPage.sizeTitle")}
                    onValueChange={handleSizeChange}
                    attachBehaviour="attached"
                    isClearEnabled={false}
                    variant="outline"
                    value={size}
                >
                    {
                        GALLERY_SIZES.map((item) => <ToggleGroup.Item
                            value={String(item)}
                            title={String(item)}
                            key={item}
                        />)
                    }
                </ToggleGroup>
                <Switch
                    title={t("brandIconsPage.rtlSwitch")}
                    onCheckedChange={setIsRtl}
                    isChecked={isRtl}
                />
            </View>
            <Preview
                caption={t("brandIconsPage.galleryCaption", [
                    filteredIcons.length
                ])}
            >
                {renderGallery()}
            </Preview>
        </Section>
        <Section
            title={t("brandIconsPage.signInTitle")}
            id="sign-in"
        >
            <Paragraph>
                {t("brandIconsPage.signInText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Button
                    title={t("brandIconsPage.signInGoogle")}
                    icon={GoogleColorBrandIcon}
                    onPress={() => undefined}
                    spreadBehaviour="stretch"
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("brandIconsPage.signInApple")}
                    onPress={() => undefined}
                    spreadBehaviour="stretch"
                    icon={AppleBrandIcon}
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("brandIconsPage.signInMicrosoft")}
                    icon={MicrosoftColorBrandIcon}
                    onPress={() => undefined}
                    spreadBehaviour="stretch"
                    variant="outline"
                    type="neutral"
                />
                <Button
                    title={t("brandIconsPage.signInGitHub")}
                    onPress={() => undefined}
                    spreadBehaviour="stretch"
                    icon={GitHubBrandIcon}
                    variant="outline"
                    type="neutral"
                />
            </Preview>
            <CodeBlock
                code={SIGN_IN_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("brandIconsPage.rulesTitle")}
            id="rules"
        >
            <BulletList
                items={[
                    t("brandIconsPage.ruleShape"),
                    t("brandIconsPage.ruleColor"),
                    t("brandIconsPage.ruleBackground"),
                    t("brandIconsPage.ruleGuidelines"),
                    t("brandIconsPage.ruleTrademark")
                ]}
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("brandIconsPage.a11yDecorative"),
                    t("brandIconsPage.a11yLabel"),
                    t("brandIconsPage.a11yButton")
                ]}
            />
        </Section>
        <Section
            title={t("brandIconsPage.apiTitle")}
            id="api"
        >
            <PropsTable
                rows={[
                    {
                        name: "GoogleBrandIcon, AppleBrandIcon, …",
                        description: t("brandIconsPage.apiMono"),
                        type: "BrandIconComponent"
                    },
                    {
                        name: "GoogleColorBrandIcon, MicrosoftColorBrandIcon",
                        description: t("brandIconsPage.apiColor"),
                        type: "BrandIconComponent"
                    },
                    {
                        description: t("brandIconsPage.apiList"),
                        type: "Array<BrandIconComponent>",
                        name: "BRAND_ICONS"
                    },
                    {
                        description: t("brandIconsPage.apiBrandColor"),
                        name: ".brandColor",
                        type: "string"
                    },
                    {
                        description: t("brandIconsPage.apiBrandTitle"),
                        name: ".brandTitle",
                        type: "string"
                    },
                    {
                        description: t("brandIconsPage.apiIsMultiColor"),
                        name: ".isMultiColor",
                        type: "boolean"
                    },
                    {
                        description: t("brandIconsPage.apiSize"),
                        type: "number | string",
                        defaultValue: "24",
                        name: "size"
                    },
                    {
                        description: t("brandIconsPage.apiColorProp"),
                        defaultValue: "\"currentColor\"",
                        type: "ColorValue",
                        name: "color"
                    },
                    {
                        description: t("brandIconsPage.apiCustomColor"),
                        name: "customColor",
                        type: "string"
                    },
                    {
                        name: "IBrandIconProps, BrandIconComponent",
                        description: t("brandIconsPage.apiTypes"),
                        type: "type"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default BrandIconsPage;
